<template>
    <DsFormField class="ds-input" :class="wrapperClass" v-bind="formFieldProps">
        <template #inputLeft="leftAttrs" v-if="iconLeft">
            <DsIcon
                :name="iconLeft"
                :width="iconSize"
                :height="iconSize"
                class="ds-form-field__input-slot-icon"
                v-bind="leftAttrs"
                :is-in-storybook
            />
        </template>

        <template #default="inputAttrs">
            <input
                ref="inputRef"
                :id
                :name="id"
                :type
                :disabled
                :required
                :aria-invalid="isInvalid"
                v-model="modelValue"
                v-maska="inputMask"
                :autocomplete="autocompleteAttr"
                v-bind="{ ...inputAttrs, ...attrs }"
            />
        </template>

        <template #inputRight="rightAttrs" v-if="iconRight">
            <DsIcon
                :name="iconRight"
                :width="iconSize"
                :height="iconSize"
                class="ds-form-field__input-slot-icon"
                :class="{ 'ds-form-field__input-slot-icon--pointer': rightIconClickFn }"
                v-bind="rightAttrs"
                @click="rightIconClickFn"
                :is-in-storybook
        /></template>

        <template #inputAfter="afterAttrs">
            <slot v-bind="afterAttrs"></slot>
        </template>
    </DsFormField>
</template>

<script setup lang="ts" generic="T extends DsFormFieldValueType">
import { computed, useAttrs, useTemplateRef } from "vue";
import { MaskInputOptions } from "maska";
import { vMaska } from "maska/vue";
import DsFormField, { stripFormFieldProps, DsFormFieldProps } from "./general/ds-form-field.vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import { DsFormFieldValueType } from "../models/ds-form-field-models";
import { SvgIcons } from "../../icon/types";

defineOptions({ inheritAttrs: false });
const { class: wrapperClass, ...attrs } = useAttrs();

/**
 * Model value of the input field.
 */
const modelValue = defineModel<T>();

const props = withDefaults(
    defineProps<
        DsFormFieldProps & {
            /**
             * Type of input field. Can be 'text', 'number', 'email', "password".
             */
            type?: "text" | "number" | "email" | "password";

            /**
             * Input mask defines how input value should be formatted. It is used to format input values based on specified pattern. Only works with 'text' type.
             */
            inputMask?: MaskInputOptions | string;

            /**
             * Icon to be displayed on the left side of the input field.
             */
            iconLeft?: SvgIcons;

            /**
             * Icon to be displayed on the right side of the input field.
             */
            iconRight?: SvgIcons;

            /**
             * Function to be executed when right icon is clicked.
             */
            rightIconClickFn?: () => void;

            /**
             * Autocomplete behavior when the input type is password.
             */
            autocompleteAttr?: AutoFill;
        }
    >(),
    { type: "text" }
);

const formFieldProps = computed(() => stripFormFieldProps(props));

const autocompleteAttr = computed(() => {
    if ((props.type === "password" || props.type === "email") && props.autocompleteAttr) {
        return props.autocompleteAttr;
    }
    return undefined;
});

const isInvalid = computed(() => !!props.errorMessage);

const inputRef = useTemplateRef<HTMLInputElement>("inputRef");

const iconSize = 24;

defineExpose({
    inputRef,
});
</script>
